import React, { FC, PropsWithoutRef } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { ThemeUIStyleObject } from 'theme-ui';
import { Image } from '~/types';
import Badge from '../Badge';

type Props = {
  alt?: string | null;
  image: Image | undefined;
  forwardSx?: ThemeUIStyleObject;
  objectFit?: 'cover' | 'contain' | 'none';
  objectPosition?: '50% 50%' | 'center' | 'top' | 'bottom' | 'left' | 'right';
  badge?: Queries.BadgeFragment | null;
} & PropsWithoutRef<JSX.IntrinsicElements['div']>;

const ImgLoader: FC<Props> = ({
  alt,
  forwardSx = {},
  image,
  objectFit = 'cover',
  objectPosition = '50% 50%',
  badge,
  ...rest
}) => {
  if (!image) return null;
  const imgAlt =
    alt ||
    (image.description && image.description.length && image.description) ||
    (image.title && image.title.length && image.title) ||
    '';
  const gatsbyImage = !!image.gatsbyImageData && getImage(image.gatsbyImageData);
  if (!gatsbyImage) return null;

  return (
    <div
      data-testid="img-loader"
      {...rest}
      sx={{
        containerType: badge ? 'size' : 'normal',
        isolation: 'isolate',
        position: 'relative',
        overflow: 'hidden',
        minHeight: '100px', // Placeholder height before image loads
        ...forwardSx,
      }}
    >
      {badge ? <Badge badge={badge} /> : null}

      <GatsbyImage
        image={gatsbyImage}
        alt={imgAlt}
        sx={{
          zIndex: 0,
          width: '100%',
          ...forwardSx,
          m: '0 !important',
        }}
        objectFit={objectFit}
        objectPosition={objectPosition}
      />
    </div>
  );
};

export default React.memo(ImgLoader);
